import { upperFirst } from 'lodash-es';
import NextImage from 'next/image';
import React, { useState } from 'react';
import { Virtual } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { blurDataURL, classNames, keyCdnCardLoader } from '@/shared/lib';
import { useInViewport } from '@/shared/lib/hooks';

import 'swiper/css';

interface PublicationImageCarouselProps {
  pictures: string[];
  publicationType: 'продажа' | 'дарение' | 'случка';
  pubTypeBgColorLookup: Record<string, string>;
  currentImageIndex: number;
  handleImageCurrentIndex: (index: number) => void;
  imageErrors: boolean[];
  onImageError: (index: number) => void;
  isAboveTheFold?: boolean;
  altBase?: string;
}

const PublicationImageCarousel: React.FC<PublicationImageCarouselProps> = ({
  pictures,
  publicationType,
  pubTypeBgColorLookup,
  currentImageIndex,
  handleImageCurrentIndex,
  onImageError,
  imageErrors,
  isAboveTheFold = false,
  altBase = 'Фото объявления'
}) => {
  const [isSwiperInitialized, setIsSwiperInitialized] = useState(false);
  const { ref, inView } = useInViewport<HTMLDivElement>({ rootMargin: '200px' });

  const handleInitializeSwiper = () => {
    if (!isSwiperInitialized) setIsSwiperInitialized(true);
  };

  const showSwiper = inView && isSwiperInitialized;

  return (
    <div ref={ref} className="relative flex">
      {!showSwiper && (
        <div className="relative h-[180px] w-full md:h-[280px]">
          <NextImage
            src={blurDataURL}
            alt={`${altBase} — предпросмотр`}
            fill
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
            priority={isAboveTheFold}
            className={classNames(
              'relative h-[180px] w-full rounded-t-2xl object-cover transition-opacity duration-500 ease-in-out md:h-[280px] md:rounded-t-4xl'
            )}
            decoding="async"
            onLoad={handleInitializeSwiper}
          />
        </div>
      )}

      {showSwiper && (
        <Swiper
          modules={[Virtual]}
          onSlideChange={(swiper) => handleImageCurrentIndex(swiper.activeIndex)}
          initialSlide={currentImageIndex}
          className="h-[180px] w-full md:h-[280px]"
        >
          {pictures.map((picture, index) => {
            const broken = imageErrors[index];
            return (
              <SwiperSlide key={`swiper-slide-grid-${index}`} className="flex h-full w-full">
                <NextImage
                  loader={keyCdnCardLoader}
                  src={broken ? '/broken-image.svg' : picture}
                  alt={`${altBase} #${index + 1}`}
                  fill
                  priority={isAboveTheFold && index === 0}
                  fetchPriority={isAboveTheFold && index === 0 ? 'high' : 'low'}
                  quality={50}
                  loading={index === 0 ? 'eager' : 'lazy'}
                  decoding="async"
                  sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                  className={classNames(
                    'h-[180px] rounded-t-2xl object-cover transition-opacity duration-500 ease-in-out md:h-[280px] md:rounded-4xl',
                    broken ? 'bg-slate-400' : ''
                  )}
                  onError={() => onImageError(index)}
                />
              </SwiperSlide>
            );
          })}

          <div
            className={`${pubTypeBgColorLookup[publicationType]} absolute inset-y-[82%] left-2 z-20 h-6 rounded-full md:left-5 md:h-8`}
          >
            {!!publicationType && (
              <p className="px-2 text-sm leading-[1.3rem] text-white md:px-4 md:leading-8">
                {upperFirst(publicationType)}
              </p>
            )}
          </div>
        </Swiper>
      )}
    </div>
  );
};

export default PublicationImageCarousel;
